/**
 * Página de escaneo de productos mediante la cámara del dispositivo.
 * Utiliza Capacitor ML Kit Barcode Scanning para obtener códigos de barras y
 * Open Food Facts para determinar la información nutricional.
 *
 * Cambios visuales v2:
 *  - Card más compacta y mejor jerarquía visual
 *  - Imagen 72px con nombre y marca al lado
 *  - Fila de scores con Nutri-Score coloreado, NOVA y Kcal
 *  - Grid nutricional 3 columnas: azúcares, sal, grasas sat., proteínas, aditivos
 *  - Ingredientes colapsables (Accordion)
 *  - Badge de saludable compacto, no invasivo
 */
import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Typography, IconButton, Fab, Card, CardContent,
    Avatar, Stack, Chip, Alert, CircularProgress,
    Accordion, AccordionSummary, AccordionDetails, Divider,
} from "@mui/material";
import CloseIcon              from "@mui/icons-material/Close";
import CameraAltIcon          from "@mui/icons-material/CameraAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon       from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon         from "@mui/icons-material/ExpandMore";
import { BarcodeScanner }     from "@capacitor-mlkit/barcode-scanning";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import foodApiService         from "../services/foodApiService";

export default function ScannerPage() {
    const [isScanning, setIsScanning] = useState(false);
    const [producto,   setProducto]   = useState(null);
    const [cargando,   setCargando]   = useState(false);
    const [error,      setError]      = useState(null);

    const stopScan = useCallback(async () => {
        document.body.classList.remove("barcode-scanner-active");
        setIsScanning(false);
        try { await BarcodeScanner.stopScan(); } catch {}
    }, []);

    const startScan = useCallback(async () => {
        try {
            document.body.classList.add("barcode-scanner-active");
            setIsScanning(true);
            setProducto(null);
            setError(null);

            const { barcodes } = await BarcodeScanner.scan();
            if (barcodes.length > 0) {
                await Haptics.impact({ style: ImpactStyle.Heavy });
                procesarBarcode(barcodes[0].displayValue);
            }
        } catch (e) {
            console.error("Error al escanear:", e);
            setError("No se pudo iniciar la cámara o el escáner.");
        } finally {
            stopScan();
        }
    }, [stopScan]);

    /** Solicita permisos e inicia el escáner al montar el componente */
    useEffect(() => {
        let isMounted = true;
        const prepareAndStart = async () => {
            try {
                const { camera } = await BarcodeScanner.checkPermissions();
                if (camera !== "granted") {
                    const { camera: newStatus } = await BarcodeScanner.requestPermissions();
                    if (newStatus === "granted" && isMounted)
                        setTimeout(() => { if (isMounted) startScan(); }, 500);
                } else {
                    if (isMounted)
                        setTimeout(() => { if (isMounted) startScan(); }, 500);
                }
            } catch (err) {
                console.error("Error al preparar el escáner:", err);
            }
        };
        prepareAndStart().catch(console.error);
        return () => { isMounted = false; stopScan(); };
    }, [startScan, stopScan]);

    /** Consulta Open Food Facts con el código detectado */
    const procesarBarcode = async (barcode) => {
        setCargando(true);
        const info = await foodApiService.getProductInfoByBarcode(barcode);
        if (info) {
            setProducto(info);
        } else {
            setError(`No se encontró información para el código: ${barcode}`);
        }
        setCargando(false);
    };

    /**
     * Color del Nutri-Score según grado (a→verde, e→rojo).
     * @param {string} grade
     */
    const getNutriColor = (grade) => ({
        a: "#038141", b: "#85bb2f", c: "#fecb02", d: "#ee8100", e: "#e63e11"
    })[(grade || "").toLowerCase()] || "#9e9e9e";

    return (
        <Box
            className={isScanning ? "barcode-scanner-active-element" : ""}
            sx={{
                p:          2,
                textAlign:  "center",
                minHeight:  "80vh",
                visibility: isScanning ? "visible" : "inherit",
            }}
        >
            {/* ── UI PRINCIPAL (sin cámara activa) ──────────────────── */}
            {!isScanning && (
                <Stack spacing={2} alignItems="center">
                    <Typography variant="h5" sx={{ fontWeight: 900 }}>
                        Escáner de Salud
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Escanea el código de barras de un alimento para saber si es saludable.
                    </Typography>

                    {/* Botón de escaneo */}
                    <Fab
                        color="primary"
                        aria-label="Escanear"
                        size="large"
                        onClick={startScan}
                        sx={{ width: 80, height: 80 }}
                    >
                        <CameraAltIcon sx={{ fontSize: 34 }} />
                    </Fab>

                    {cargando && <CircularProgress size={36} />}

                    {error && (
                        <Alert severity="warning" sx={{ width: "100%", borderRadius: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {/* ── TARJETA DE RESULTADO ────────────────────────── */}
                    {producto && (
                        <Card sx={{ width: "100%", borderRadius: 3, textAlign: "left" }} elevation={3}>
                            <CardContent sx={{ p: "14px !important" }}>
                                <Stack spacing={1.5}>

                                    {/* Fila: imagen + nombre + marca */}
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Avatar
                                            src={producto.imagen}
                                            variant="rounded"
                                            sx={{ width: 72, height: 72, flexShrink: 0, bgcolor: "grey.100" }}
                                        />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight:   800,
                                                    lineHeight:   1.2,
                                                    overflow:     "hidden",
                                                    textOverflow: "ellipsis",
                                                    display:      "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical",
                                                }}
                                            >
                                                {producto.nombre}
                                            </Typography>
                                            {producto.marca && (
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                    {producto.marca}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>

                                    <Divider />

                                    {/* Fila de scores: Nutri-Score | NOVA | Kcal */}
                                    <Stack direction="row" justifyContent="space-around" alignItems="center">
                                        {/* Nutri-Score */}
                                        <Box sx={{ textAlign: "center" }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                Nutri-Score
                                            </Typography>
                                            <Chip
                                                label={producto.nutriscore !== "unknown"
                                                    ? producto.nutriscore.toUpperCase()
                                                    : "?"}
                                                size="small"
                                                sx={{
                                                    bgcolor:    getNutriColor(producto.nutriscore),
                                                    color:      "white",
                                                    fontWeight: 900,
                                                    fontSize:   "0.85rem",
                                                    minWidth:   36,
                                                }}
                                            />
                                        </Box>

                                        {/* NOVA */}
                                        <Box sx={{ textAlign: "center" }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                NOVA
                                            </Typography>
                                            <Avatar
                                                sx={{
                                                    width:   32,
                                                    height:  32,
                                                    mx:      "auto",
                                                    fontSize: 14,
                                                    fontWeight: 900,
                                                    bgcolor: producto.nova
                                                        ? ["#4caf50","#8bc34a","#ff9800","#f44336"][producto.nova - 1]
                                                        : "#9e9e9e",
                                                }}
                                            >
                                                {producto.nova || "?"}
                                            </Avatar>
                                        </Box>

                                        {/* Kcal */}
                                        <Box sx={{ textAlign: "center" }}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Kcal (100g)
                                            </Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                                {producto.kcal ?? "–"}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Divider />

                                    {/* Grid nutricional: 3 columnas */}
                                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0.75 }}>
                                        {[
                                            { label: "Azúcares",   valor: producto.azucares,  unidad: "g" },
                                            { label: "Sal",        valor: producto.sal,        unidad: "g" },
                                            { label: "Grasas sat.", valor: producto.grasas,    unidad: "g" },
                                            { label: "Proteínas",  valor: producto.proteinas,  unidad: "g" },
                                            { label: "Aditivos",   valor: producto.aditivos,   unidad: ""  },
                                        ].map(({ label, valor, unidad }) => (
                                            <Box
                                                key={label}
                                                sx={{
                                                    p:           1,
                                                    bgcolor:     "rgba(0,0,0,0.03)",
                                                    borderRadius: 2,
                                                    textAlign:   "center",
                                                }}
                                            >
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {label}
                                                </Typography>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                    {valor ?? "–"}{unidad}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>

                                    {/* Badge saludable — compacto */}
                                    <Box
                                        sx={{
                                            p:           "10px 14px",
                                            borderRadius: 2,
                                            display:     "flex",
                                            alignItems:  "center",
                                            gap:         1,
                                            bgcolor:     producto.es_saludable
                                                ? "rgba(76,175,80,0.1)"
                                                : "rgba(244,67,54,0.1)",
                                            border:      "1.5px solid",
                                            borderColor: producto.es_saludable ? "success.main" : "error.main",
                                            color:       producto.es_saludable ? "success.main" : "error.main",
                                        }}
                                    >
                                        {producto.es_saludable
                                            ? <CheckCircleOutlineIcon fontSize="small" />
                                            : <ErrorOutlineIcon fontSize="small" />}
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                                {producto.es_saludable ? "Producto saludable" : "No recomendado / Ocasional"}
                                            </Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                {producto.es_saludable
                                                    ? "Buena opción para tu cesta."
                                                    : "Alto procesado o ingredientes no recomendables."}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Ingredientes colapsables — solo si hay datos */}
                                    {producto.ingredientes && (
                                        <Accordion
                                            disableGutters
                                            elevation={0}
                                            sx={{
                                                border:       "1px solid",
                                                borderColor:  "divider",
                                                borderRadius: "8px !important",
                                                "&:before":   { display: "none" },
                                            }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, py: 0 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                                    Ver ingredientes
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ pt: 0 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                    {producto.ingredientes}
                                                </Typography>
                                            </AccordionDetails>
                                        </Accordion>
                                    )}

                                </Stack>
                            </CardContent>
                        </Card>
                    )}
                </Stack>
            )}

            {/* ── OVERLAY CÁMARA ACTIVA ─────────────────────────────── */}
            {isScanning && (
                <Box
                    className="barcode-scanner-active-element"
                    sx={{
                        position:       "fixed",
                        top: 0, left: 0,
                        width:          "100%",
                        height:         "100%",
                        zIndex:         9999,
                        display:        "flex",
                        flexDirection:  "column",
                        justifyContent: "space-between",
                        p:              3,
                        visibility:     "visible",
                    }}
                >
                    {/* Botón cerrar */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconButton
                            onClick={stopScan}
                            sx={{ color: "white", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                        >
                            <CloseIcon fontSize="large" />
                        </IconButton>
                    </Box>

                    {/* Marco de escaneo */}
                    <Box
                        sx={{
                            alignSelf:  "center",
                            width:      "260px",
                            height:     "260px",
                            border:     "2px solid white",
                            borderRadius: "20px",
                            boxShadow:  "0 0 0 9999px rgba(0,0,0,0.5)",
                        }}
                    />

                    {/* Instrucción */}
                    <Box sx={{ textAlign: "center", mb: 8 }}>
                        <Typography variant="h6" sx={{ color: "white", textShadow: "1px 1px 4px rgba(0,0,0,0.8)" }}>
                            Centra el código de barras
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
}